import * as THREE from 'three';
import {AbstractPeriodicIntersectionSolver} from './AbstractPeriodicIntersectionSolver';
import type {LineSegmentIntersectionDefinition2D} from './CircleLineSegmentIntersectionSolver';
import type {EllipseIntersectionDefinition2D} from './EllipseCircleIntersectionSolver';

/**
 * 椭圆与有限线段的单个真实交点。
 */
export interface EllipseLineSegmentIntersection2D {
    /** 二维交点坐标。 */
    point: THREE.Vector2;
    /** 交点在完整椭圆参数方程上的角参数，范围 `[0, 2π)`。 */
    ellipseAngle: number;
    /** 交点在有限线段上的参数，范围 `[0, 1]`。 */
    lineParameter: number;
}

/**
 * 椭圆与有限线段的二维真实交点求解器。
 *
 * 职责：
 * 1. 把线段起终点转换到椭圆标准方程坐标系；
 * 2. 将线段参数方程代入单位圆隐式方程并求解二次方程；
 * 3. 过滤有限线段范围外的支撑直线根；
 * 4. 返回交点、椭圆角参数和线段参数。
 *
 * 该类只处理二维数学定义，不依赖草图对象、参考面或显示采样几何。
 */
export class EllipseLineSegmentIntersectionSolver extends AbstractPeriodicIntersectionSolver {
    /** 二次方程判别式相对容差。 */
    private static readonly DISCRIMINANT_RELATIVE_TOLERANCE = 1e-12;

    /** 线段参数域边界容差。 */
    private static readonly PARAMETER_TOLERANCE = 1e-10;

    /** 退化线段相对容差。 */
    private static readonly DEGENERATE_LINE_RELATIVE_TOLERANCE = 1e-12;

    /** 交点隐式方程残差容差。 */
    private static readonly IMPLICIT_RESIDUAL_TOLERANCE = 1e-10;

    /** 世界交点去重相对容差。 */
    private static readonly POINT_RELATIVE_TOLERANCE = 1e-10;

    /**
     * 求解椭圆与有限线段的全部真实交点。
     *
     * @param ellipse 二维椭圆定义。
     * @param lineSegment 二维有限线段定义。
     * @returns 按椭圆角参数升序排列的真实交点。
     */
    public solve(
        ellipse: EllipseIntersectionDefinition2D,
        lineSegment: LineSegmentIntersectionDefinition2D,
    ): EllipseLineSegmentIntersection2D[] {
        if (!this.hasValidDefinition(ellipse, lineSegment)) {
            console.error('[EllipseLineSegmentIntersectionSolver] 椭圆或线段定义无效，无法计算真实交点。', {
                ellipse,
                lineSegment,
            });
            return [];
        }

        const direction = lineSegment.end.clone().sub(lineSegment.start);
        const geometryScale = Math.max(
            ellipse.radiusX,
            ellipse.radiusY,
            lineSegment.start.distanceTo(ellipse.center),
            lineSegment.end.distanceTo(ellipse.center),
        );
        const degenerateLineTolerance = Math.max(
            geometryScale * EllipseLineSegmentIntersectionSolver.DEGENERATE_LINE_RELATIVE_TOLERANCE,
            Number.EPSILON,
        );
        if (direction.lengthSq() <= degenerateLineTolerance * degenerateLineTolerance) {
            console.error('[EllipseLineSegmentIntersectionSolver] 线段退化为点，无法计算真实交点。', {
                ellipse,
                lineSegment,
            });
            return [];
        }

        const canonicalStart = this.transformPointToCanonical(ellipse, lineSegment.start);
        const canonicalEnd = this.transformPointToCanonical(ellipse, lineSegment.end);
        const canonicalDirection = canonicalEnd.clone().sub(canonicalStart);

        // 将 `|P0 + tD|² = 1` 展开为 `At² + Bt + C = 0`。
        const quadraticA = canonicalDirection.lengthSq();
        const quadraticB = 2 * canonicalStart.dot(canonicalDirection);
        const quadraticC = canonicalStart.lengthSq() - 1;
        const discriminant = quadraticB * quadraticB - 4 * quadraticA * quadraticC;
        const discriminantScale = Math.max(
            1,
            quadraticB * quadraticB,
            Math.abs(4 * quadraticA * quadraticC),
        );
        const discriminantTolerance = discriminantScale
            * EllipseLineSegmentIntersectionSolver.DISCRIMINANT_RELATIVE_TOLERANCE;
        if (discriminant < -discriminantTolerance) {
            return [];
        }

        const normalizedDiscriminant = Math.max(0, discriminant);
        const squareRoot = Math.sqrt(normalizedDiscriminant);
        const lineParameters = normalizedDiscriminant <= discriminantTolerance
            ? [-quadraticB / (2 * quadraticA)]
            : [
                (-quadraticB - squareRoot) / (2 * quadraticA),
                (-quadraticB + squareRoot) / (2 * quadraticA),
            ];
        const pointTolerance = Math.max(
            geometryScale * EllipseLineSegmentIntersectionSolver.POINT_RELATIVE_TOLERANCE,
            Number.EPSILON,
        );
        const pointToleranceSquared = pointTolerance * pointTolerance;
        const intersections: EllipseLineSegmentIntersection2D[] = [];

        lineParameters.forEach((lineParameter) => {
            if (
                lineParameter < -EllipseLineSegmentIntersectionSolver.PARAMETER_TOLERANCE
                || lineParameter > 1 + EllipseLineSegmentIntersectionSolver.PARAMETER_TOLERANCE
            ) {
                return;
            }

            const normalizedLineParameter = THREE.MathUtils.clamp(lineParameter, 0, 1);
            const canonicalPoint = canonicalStart.clone().addScaledVector(
                canonicalDirection,
                normalizedLineParameter,
            );
            if (
                Math.abs(canonicalPoint.lengthSq() - 1)
                > EllipseLineSegmentIntersectionSolver.IMPLICIT_RESIDUAL_TOLERANCE
            ) {
                return;
            }

            const point = lineSegment.start.clone().addScaledVector(direction, normalizedLineParameter);
            if (intersections.some((candidate) => candidate.point.distanceToSquared(point) <= pointToleranceSquared)) {
                return;
            }

            intersections.push({
                point,
                ellipseAngle: this.normalizeAngle(Math.atan2(canonicalPoint.y, canonicalPoint.x)),
                lineParameter: normalizedLineParameter,
            });
        });

        return intersections.sort((left, right) => left.ellipseAngle - right.ellipseAngle);
    }

    /**
     * 判断椭圆与线段定义是否可参与解析求交。
     *
     * @param ellipse 椭圆定义。
     * @param lineSegment 线段定义。
     * @returns 所有坐标有限且椭圆半轴为正时返回 true。
     */
    private hasValidDefinition(
        ellipse: EllipseIntersectionDefinition2D,
        lineSegment: LineSegmentIntersectionDefinition2D,
    ): boolean {
        return Number.isFinite(ellipse.center.x)
            && Number.isFinite(ellipse.center.y)
            && Number.isFinite(ellipse.radiusX)
            && ellipse.radiusX > 0
            && Number.isFinite(ellipse.radiusY)
            && ellipse.radiusY > 0
            && Number.isFinite(ellipse.rotation)
            && Number.isFinite(lineSegment.start.x)
            && Number.isFinite(lineSegment.start.y)
            && Number.isFinite(lineSegment.end.x)
            && Number.isFinite(lineSegment.end.y);
    }

    /**
     * 将二维点转换到椭圆单位圆标准方程坐标系。
     *
     * @param ellipse 椭圆定义。
     * @param point 原始二维点。
     * @returns 椭圆标准方程坐标。
     */
    private transformPointToCanonical(
        ellipse: EllipseIntersectionDefinition2D,
        point: THREE.Vector2,
    ): THREE.Vector2 {
        const translatedX = point.x - ellipse.center.x;
        const translatedY = point.y - ellipse.center.y;
        const cosineRotation = Math.cos(ellipse.rotation);
        const sineRotation = Math.sin(ellipse.rotation);
        return new THREE.Vector2(
            (cosineRotation * translatedX + sineRotation * translatedY) / ellipse.radiusX,
            (-sineRotation * translatedX + cosineRotation * translatedY) / ellipse.radiusY,
        );
    }

}
